import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { ContactSyncModel } from '../../../../../types';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Apple from '@mui/icons-material/Apple';

interface StepOneProps {
  control: Control<Partial<ContactSyncModel>>;
  errors: FieldErrors<Partial<ContactSyncModel>>;
  setValue: UseFormSetValue<Partial<ContactSyncModel>>;
  watch: UseFormWatch<Partial<ContactSyncModel>>;
  featureAccess: string[];
  isNameUnique: (name: string) => boolean;
}

export default function StepOne({
  control,
  errors,
  watch,
  setValue,
  featureAccess,
  isNameUnique,
}: StepOneProps) {
  let syncTypeOptions = [
    { displayValue: 'Personal Contacts', value: 'contact' },
    { displayValue: 'Directory Contacts', value: 'directory' },
  ];

  if (featureAccess.includes('bi-directionalcontactsync')) {
    syncTypeOptions = [
      { displayValue: 'Personal Contacts', value: 'contact' },
      { displayValue: 'Directory Contacts', value: 'directory' },
      { displayValue: 'Bi-Directional Sync', value: 'bidirectional' },
    ];
  }

  const syncType = watch('syncType') || '';

  return (
    <Box
      sx={{
        viewTransitionName: 'steps',
      }}
    >
      <Stack gap={2} sx={{ pt: 4 }}>
        <Stack flexDirection="row" gap={4}>
          <Controller
            name="name"
            control={control}
            rules={{
              required: 'Name is required.',
            }}
            render={({ field }) => (
              <TextField
                required
                {...field}
                autoComplete="off"
                inputProps={{
                  id: 'name',
                }}
                InputLabelProps={{
                  htmlFor: 'name',
                }}
                error={!!errors.name}
                helperText={errors?.name?.message || ' '}
                label="Contact Sync Name"
                sx={{ width: 400 }}
              />
            )}
          />
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                inputProps={{
                  id: 'description',
                }}
                InputLabelProps={{
                  htmlFor: 'description',
                }}
                label="Sync Description"
                sx={{ width: 400 }}
              />
            )}
          />
        </Stack>
        <Stack flexDirection="row" gap={4}>
          <Controller
            name="syncType"
            control={control}
            render={({ field }) => (
              <TextField
                inputProps={{
                  id: 'syncType',
                }}
                InputLabelProps={{
                  htmlFor: 'syncType',
                }}
                select
                label="Contact Sync Type"
                sx={{ width: 400 }}
                value={field.value || ''}
                onChange={(e) => {
                  field.onChange(e.target.value);
                  if (
                    e.target.value === 'contact' ||
                    e.target.value === 'bidirectional'
                  ) {
                    setValue('respectDirectoryContactSharing', false);
                  }
                }}
              >
                {syncTypeOptions.map((option) => (
                  <MenuItem value={option.value} key={option.displayValue}>
                    {option.displayValue}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            name="respectDirectoryContactSharing"
            control={control}
            render={({ field: props }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    {...props}
                    checked={props.value}
                    disabled={
                      watch('syncType') === 'contact' ||
                      watch('syncType') === 'bidirectional'
                    }
                    onChange={(e) => props.onChange(e.target.checked)}
                  />
                }
                label={`Respect "Contact Sharing" setting`}
              />
            )}
          />
          <Controller
            name="addToMyContacts"
            control={control}
            render={({ field: props }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    {...props}
                    checked={props.value}
                    onChange={(e) => props.onChange(e.target.checked)}
                  />
                }
                label={`Add to "My Contacts"`}
              />
            )}
          />
        </Stack>
        {syncType === 'directory' && (
          <Stack flexDirection="row" gap={4} sx={{ pt: 2 }}>
            <Controller
              name="targetGroupName"
              control={control}
              rules={{
                required: 'Target label name is required.',
                validate: (value) =>
                  isNameUnique(value || '') ||
                  "Label name must be unique. It can't match other contact sync target labels.",
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Target Label Name"
                  error={!!errors.targetGroupName}
                  helperText={
                    errors?.targetGroupName?.message ||
                    'Name of the label to be created for the contact sync.'
                  }
                  required
                  sx={{ width: 400 }}
                />
              )}
            />
          </Stack>
        )}

        <Controller
          name="scheduled"
          control={control}
          render={({ field: props }) => (
            <FormControlLabel
              control={
                <Checkbox
                  {...props}
                  checked={props.value}
                  onChange={(e) => props.onChange(e.target.checked)}
                />
              }
              label="Scheduled Run"
            />
          )}
        />
      </Stack>
      <Alert severity="info" sx={{ mt: 2 }}>
        <AlertTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Apple /> iOS Contact Duplications
        </AlertTitle>
        We’re aware of an issue with iPhone contacts being duplicated when using
        gPanel contact syncs. Apple has recently modified how they handle
        contact duplications. We’re in the process of creating a new gPanel
        feature for contacts to address this issue.
      </Alert>
    </Box>
  );
}
